import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, ReactNode } from 'react';
import { useAuth } from 'contexts/AuthContext';
import { favouriteService } from 'services/favourite.service';

interface FavouritesContextType {
  /** Saved variant IDs, newest first. Empty when signed out. */
  variantIds: number[];
  isLoading: boolean;
  isFavourite: (variantId: number) => boolean;
  /** Saves or removes, depending on current state. Resolves once settled. */
  toggleFavourite: (variantId: number) => Promise<void>;
  refresh: () => Promise<void>;
}

const FavouritesContext = createContext<FavouritesContextType | undefined>(undefined);

export const useFavourites = () => {
  const context = useContext(FavouritesContext);
  if (context === undefined) {
    throw new Error('useFavourites must be used within a FavouritesProvider');
  }
  return context;
};

/**
 * Holds the customer's saved variant IDs for the whole session.
 *
 * The set is fetched once on sign-in rather than per product, so a grid of
 * cards costs one request instead of one per card. Toggling updates local
 * state first and rolls back if the request fails.
 */
export const FavouritesProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [variantIds, setVariantIds] = useState<number[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!isAuthenticated) {
      setVariantIds([]);
      return;
    }
    setIsLoading(true);
    try {
      const { variantIds: ids } = await favouriteService.getFavourites();
      setVariantIds(ids);
    } catch (error) {
      console.error('Failed to load saved items:', error);
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated]);

  // Load on sign-in; clear on sign-out so the next customer never sees the
  // previous one's saved items.
  useEffect(() => {
    void refresh();
  }, [refresh]);

  const favouriteSet = useMemo(() => new Set(variantIds), [variantIds]);

  const isFavourite = useCallback(
    (variantId: number) => favouriteSet.has(variantId),
    [favouriteSet]
  );

  const toggleFavourite = useCallback(
    async (variantId: number) => {
      if (!isAuthenticated) return;

      const wasFavourite = favouriteSet.has(variantId);
      // Optimistic: the heart fills immediately, then rolls back on failure.
      setVariantIds((previous) =>
        wasFavourite
          ? previous.filter((id) => id !== variantId)
          : [variantId, ...previous]
      );

      try {
        if (wasFavourite) {
          await favouriteService.removeFavourite(variantId);
        } else {
          await favouriteService.addFavourite(variantId);
        }
      } catch (error) {
        console.error('Failed to update saved items:', error);
        setVariantIds((previous) =>
          wasFavourite
            ? [variantId, ...previous.filter((id) => id !== variantId)]
            : previous.filter((id) => id !== variantId)
        );
      }
    },
    [isAuthenticated, favouriteSet]
  );

  const value = useMemo(
    () => ({ variantIds, isLoading, isFavourite, toggleFavourite, refresh }),
    [variantIds, isLoading, isFavourite, toggleFavourite, refresh]
  );

  return <FavouritesContext.Provider value={value}>{children}</FavouritesContext.Provider>;
};
